import { CommonModule, NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';
import { APP_CONSTANTS } from 'app/shared/constants/app.constants';
import { ClockInOutComponent } from 'app/layout/common/clock-in-out/clock-in-out.component';
import { AuthService } from 'app/core/auth/auth.service';
import { CommonService } from 'app/shared/services/common/common.service';
import { environment } from 'environments/environment';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { jwtDecode } from "jwt-decode";
import { C } from '@fullcalendar/core/internal-common';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatSelectModule,
        MatMenuModule,
        MatButtonModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        NotificationsComponent,
        UserComponent,
        NgIf,
        RouterOutlet,
        QuickChatComponent,
        ClockInOutComponent,
        MatTooltip
    ]
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    @ViewChild('quickChat') quickChat!: QuickChatComponent;
    @ViewChild(MatTooltip) tooltip: MatTooltip;
    isScreenSmall: boolean;
    navigation: Navigation;
    product_name = APP_CONSTANTS.PRODUCT_NAME;
    version = APP_CONSTANTS.VERSION;
    userInfo: any;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    logoUrl = environment.vortexLogo;
    dropdownOpen = false;
    selectedRole: string = '';
    secondaryRoleList: any;
    secondaryRoles: any;
    selectedRoleId: string = '';
    selectedRoleName: string = '';
    isOverflow: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _authService: AuthService,
        private _commonService: CommonService,
        private _router: Router,
        private _ngZone: NgZone
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.userInfo = this._authService.userInfo;
        this.secondaryRoles = this.userInfo.secondary_roles || [];
        this.selectedRole = this.userInfo.roleName || '';

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    async onRoleSelected(role: any) {
        try {
            this.selectedRoleId = role._id;
            this.selectedRoleName = role.name;
            await this._authService.getAuthToken(this.userInfo._id, this.selectedRoleId).toPromise();

            setTimeout(() => {
                this.userInfo = this._authService.userInfo;
                this.secondaryRoles = this.userInfo.secondary_roles || [];
                this._navigationService.get();
            }, 500);

            this._ngZone.run(() => {
                this._router.navigate(['/home']);
            });

            this._changeDetectorRef.detectChanges();
        } catch (error) {
            console.error('Error while changing role:', error);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    toggleDropdown(): void {
        this.dropdownOpen = !this.dropdownOpen;
    }

    onMouseEnter(element: HTMLElement, tooltip: MatTooltip): void {
        this.checkOverflow(element);
        if (this.isOverflow) {
            tooltip.message = element.innerText.trim();
            tooltip.show();
        } else {
            tooltip.hide();
        }
    }

    checkOverflow(element: HTMLElement): void {
        this.isOverflow = element.scrollWidth > element.clientWidth;
        this._changeDetectorRef.detectChanges();
    }
    drawerOpen = false; // Tracks whether the drawer is open or not

    // Method to handle drawer state change
    onDrawerStateChange(isOpen: boolean): void {
        this.drawerOpen = isOpen;
        console.log('Drawer is open:', this.drawerOpen);
        // Apply any logic or state changes when the drawer opens/closes
    }
}
